import React, { useCallback, useMemo, useState } from "react";
import get from "lodash/get";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import upperCase from "lodash/upperCase";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import { CollapsibleMenu } from "@whatslly/ui-components/CollapsibleMenu";
import { useTranslation } from "react-i18next";
import styles from "./entity-filter.module.scss";
import { Switch } from "../v2/BaseElements/Switch";
import classnames from "classnames";
import { TextInput } from "../v2/BaseElements/TextInput";

export type BooleanFiltersSettingsType = {
  onChange: (newState: boolean) => void;
  value: boolean;
  text: string;
  defaultValue: boolean;
  testId?: string;
  disabled?: boolean;
};
export type FilterSettingsType = {
  title: string;
  searchKey: string;
  maskMe?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customDefaultState?: (value: any) => boolean;
} & ( // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | { customLabel: (value: any) => string; searchLabel?: undefined; customObjectLabel?: undefined }
  | { searchLabel: string; customLabel?: undefined; customObjectLabel?: undefined }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | { customObjectLabel: Record<string, any>; customLabel?: undefined; searchLabel?: undefined }
);

export const EntityFilter: React.FC<{
  filterSettings: FilterSettingsType[] | undefined;
  currentUserId?: string;
  testId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entities: Record<string, any>[];
  EntityFilterButton: React.FC<{ isOpen: boolean; numberOfActiveFilters: number | undefined; disabled?: boolean }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectedFiltersChanged?: (selected: Record<string, any[]>) => void;
  title: string;
  initFilteredFields?: Record<string, (string | number)[]>;
  collapsibleMenuClassName?: string;
  BooleanFilters?: BooleanFiltersSettingsType[];
  hideButtons?: boolean;
  hideClearAll?: boolean;
  hideResetFilter?: boolean;
  textEllipsis?: boolean;
  useSearchInput?: () => { searchText: string; setSearchText: (newSearchText: string) => void };
  disabled?: boolean;
  oppositeCheckedPosition?: boolean;
}> = ({
  filterSettings,
  currentUserId,
  entities,
  EntityFilterButton,
  onSelectedFiltersChanged,
  title,
  testId,
  initFilteredFields,
  collapsibleMenuClassName,
  BooleanFilters,
  hideButtons,
  hideClearAll,
  hideResetFilter,
  textEllipsis,
  useSearchInput,
  disabled,
  oppositeCheckedPosition,
}) => {
  const [filteredFields, setFilteredFields] = useState<Record<string, (string | number)[]>>(initFilteredFields ?? {});
  const { t } = useTranslation();
  const searchInputSettings = useSearchInput?.();

  const filterSettingsToSend = useMemo(() => {
    return filterSettings
      ?.map((filterSetting) => {
        const items = (() => {
          if (filterSetting.customObjectLabel) {
            return Object.entries(filterSetting.customObjectLabel).map(([value, label]) => ({ label, value }));
          }
          return uniqBy(
            entities
              .map((item) => {
                if (filterSetting.customDefaultState) {
                  setFilteredFields({
                    ...filteredFields,
                    [filterSetting.searchKey]: filteredFields[filterSetting.searchKey]
                      ? uniq([...filteredFields[filterSetting.searchKey], filterSetting.customDefaultState(get(item, filterSetting.searchKey))])
                      : [get(item, filterSetting.searchKey)],
                  });
                }
                const label = filterSetting.customLabel
                  ? filterSetting.customLabel(get(item, filterSetting.searchKey))
                  : get(item, filterSetting.searchLabel);
                if (!label) {
                  return undefined as unknown as { label: string; value: string };
                }

                return {
                  label,
                  value: get(item, filterSetting.searchKey),
                };
              })
              .filter(Boolean),
            `value`
          );
        })();
        if (items.length === 0) {
          return undefined as unknown as FilterSettingsType & { items: { label: string; value: string }[] };
        }
        return {
          ...filterSetting,
          ...{
            items,
          },
        };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, filterSettings, searchInputSettings?.searchText]);

  const onClearAll = useCallback(() => {
    const filtersSettings =
      filterSettingsToSend?.reduce<Record<string, string[]>>((acc, setting) => {
        acc[setting.searchKey] = setting.items.map((item) => item.value);
        return acc;
      }, {}) || {};
    setFilteredFields(filtersSettings);
    onSelectedFiltersChanged?.(filtersSettings);
    BooleanFilters?.forEach((booleanFilter) => {
      booleanFilter.onChange(false);
    });
  }, [BooleanFilters, filterSettingsToSend, onSelectedFiltersChanged]);

  const onResetFilter = useCallback(() => {
    setFilteredFields({});
    onSelectedFiltersChanged?.({});
    BooleanFilters?.forEach((booleanFilter) => {
      booleanFilter.onChange(booleanFilter.defaultValue);
    });
  }, [BooleanFilters, onSelectedFiltersChanged]);

  const numberOfActiveFilters = useMemo(() => {
    return Object.values(filteredFields).reduce((numberOfFilters, activeFiltersArray) => {
      numberOfFilters += activeFiltersArray?.length ?? 0;
      return numberOfFilters;
    }, 0);
  }, [filteredFields]);

  const onSelectedFilter = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, setting: FilterSettingsType, item: { label: string; value: string }) => {
      const newValue = (() => {
        const newState = { ...filteredFields };

        if (oppositeCheckedPosition) {
          if (newState[setting.searchKey]) {
            if (!event.target.checked) {
              newState[setting.searchKey] = newState[setting.searchKey].filter((itemValue) => item.value !== itemValue);
            } else {
              newState[setting.searchKey].push(item.value);
            }
            return newState;
          }
          newState[setting.searchKey] = [item.value];
          return { ...newState };
        }
        if (newState[setting.searchKey]) {
          if (!event.target.checked) {
            newState[setting.searchKey].push(item.value);
          } else {
            newState[setting.searchKey] = newState[setting.searchKey].filter((itemValue) => item.value !== itemValue);
          }
          return newState;
        }
        newState[setting.searchKey] = [item.value];
        return { ...newState };
      })();
      setFilteredFields(newValue);
      onSelectedFiltersChanged?.(newValue);
    },
    [filteredFields, onSelectedFiltersChanged, oppositeCheckedPosition]
  );

  const lengthOfTotalFilteredBySearch = useMemo(() => {
    const booleanFilterSearchTextAsLabel = BooleanFilters ? [{ items: BooleanFilters?.map((filter) => ({ label: filter.text })) }] : [];
    return [...(filterSettingsToSend || []), ...booleanFilterSearchTextAsLabel]?.reduce(
      (acc, setting) => {
        acc.itemsLength += setting.items.length;
        acc.filteredLength += setting.items.filter(
          (item) =>
            searchInputSettings?.searchText.length !== 0 &&
            typeof item.label === "string" &&
            item.label?.toLowerCase().includes(searchInputSettings?.searchText.toLocaleLowerCase() || "")
        ).length;
        return acc;
      },
      { itemsLength: 0, filteredLength: 0 }
    );
  }, [BooleanFilters, filterSettingsToSend, searchInputSettings?.searchText]);

  return (
    <CollapsibleMenu
      title={title}
      button={({ isOpen }) => (
        <div data-testid={testId}>
          {((filterSettings && filterSettings.length > 0) || (BooleanFilters && BooleanFilters.length > 0)) && (
            <EntityFilterButton isOpen={isOpen} numberOfActiveFilters={numberOfActiveFilters} disabled={disabled} />
          )}
        </div>
      )}
      menuClassName={collapsibleMenuClassName}
      disabled={disabled}
    >
      {searchInputSettings && (
        <div className={styles.searchFilterContainer}>
          <TextInput
            inputSize="small"
            paddingInline="none"
            placeholder={t("search")}
            borderRadius="small"
            marginBottom="small"
            defaultValue={searchInputSettings?.searchText}
            onClear={() => {
              searchInputSettings.setSearchText("");
            }}
            onChange={(e) => {
              searchInputSettings.setSearchText(e.target.value);
            }}
          />
        </div>
      )}
      {((searchInputSettings && lengthOfTotalFilteredBySearch?.itemsLength === 0) ||
        (lengthOfTotalFilteredBySearch?.filteredLength === 0 && (searchInputSettings?.searchText?.length || 0) !== 0)) && (
        <div className={styles.noResultsContainer}>
          <div>{t("common.crm_table.no_results_found")}</div>
        </div>
      )}
      <div className={styles.filtersWrapper}>
        {BooleanFilters && (
          <div className={classnames(styles.booleanFiltersWrapper, styles.filtersGroupsContainer)}>
            {BooleanFilters.map((booleanFilter, key) => {
              if (
                searchInputSettings?.searchText.length === 0 ||
                (typeof booleanFilter.text === "string" &&
                  booleanFilter.text?.toLowerCase().includes(searchInputSettings?.searchText.toLocaleLowerCase() || ""))
              ) {
                return (
                  <div className={styles.booleanFilterContainer} key={`${key}`}>
                    <Switch
                      testId={booleanFilter.testId}
                      checked={booleanFilter.value}
                      onChange={(e) => {
                        booleanFilter.onChange(e.target.checked);
                      }}
                      disabled={booleanFilter.disabled}
                    />
                    <p>{booleanFilter.text}</p>
                  </div>
                );
              }
              return <></>;
            })}
          </div>
        )}
        {filterSettingsToSend && filterSettingsToSend.length > 0 && (
          <div className={classnames(styles.filtersContainer, { [styles.filtersContainerWithBooleanFilters]: !!BooleanFilters })}>
            {filterSettingsToSend.map((setting, i) => {
              const settingFilteredItems = setting.items.filter(
                (item) =>
                  !searchInputSettings?.searchText ||
                  (searchInputSettings?.searchText.length !== 0 &&
                    typeof item.label === "string" &&
                    item.label?.toLowerCase().includes(searchInputSettings?.searchText.toLocaleLowerCase() || ""))
              );
              return (
                settingFilteredItems.length > 0 && (
                  <div className={styles.filtersGroupsContainer} key={i}>
                    <div className={styles.filtersGroupHeader}>{upperCase(setting.title)}</div>
                    {settingFilteredItems.map((item, j) => {
                      return (
                        (searchInputSettings?.searchText.length === 0 ||
                          (typeof item.label === "string" &&
                            item.label?.toLowerCase().includes(searchInputSettings?.searchText.toLocaleLowerCase() || ""))) && (
                          <div data-testid="filter-item" className={styles.filter} key={`${j}`}>
                            <CheckBox
                              data-testid={`filter-item-${item.value}-checkbox`}
                              disableUserSelect
                              checked={
                                oppositeCheckedPosition
                                  ? filteredFields[setting.searchKey]?.includes(item.value)
                                  : !filteredFields[setting.searchKey]?.includes(item.value)
                              }
                              text={setting.maskMe ? (item.value === currentUserId ? t("entity_filter.me") : item.label) : item.label}
                              onChange={(e) => onSelectedFilter(e, setting, item)}
                              clickAbleLabel={true}
                              textEllipsis={textEllipsis}
                            />
                          </div>
                        )
                      );
                    })}
                  </div>
                )
              );
            })}
          </div>
        )}
      </div>
      <div>
        {!hideButtons && (!hideResetFilter || !hideClearAll) && (
          <div className={styles.filtersClearAll}>
            {!hideClearAll && <span onClick={oppositeCheckedPosition ? onResetFilter : onClearAll}>{t("entity_filter.clear_all")}</span>}
            {!hideResetFilter && <span onClick={oppositeCheckedPosition ? onClearAll : onResetFilter}>{t("entity_filter.reset_filter")}</span>}
          </div>
        )}
      </div>
    </CollapsibleMenu>
  );
};
